import { SortAsc, SortDesc } from "lucide-react";
import type React from "react";
import { useCallback, useMemo } from "react";
import { TableHead } from "./table";
import type { ColumnDef } from "./types";

interface DataTableHeaderProps<T = unknown> {
	column: ColumnDef<T>;
	onSort?: () => void;
	isSorted: boolean;
	isSortedDesc: boolean;
}

export function DataTableHeader<T>({
	column,
	onSort,
	isSorted,
	isSortedDesc,
}: DataTableHeaderProps<T>) {
	// Determine if this column is sortable (unless explicitly false)
	const isSortable = column.sortable !== false;

	// For debugging - log the current sort state for this column
	// useEffect(() => {
	// 	if (isSorted) {
	// 		console.log(
	// 			`🔍 Column ${column.id} is sorted ${isSortedDesc ? "DESC" : "ASC"}`,
	// 		);
	// 	}
	// }, [column.id, isSorted, isSortedDesc]);

	// Memoize the click handler for better performance
	const handleClick = useCallback(
		(e: React.MouseEvent) => {
			e.preventDefault(); // Prevent any default behavior
			e.stopPropagation(); // Prevent event bubbling

			// Only call onSort if onSort is provided and column is sortable
			if (onSort && isSortable) {
				console.log(`🖱️ Header clicked: column=${column.id}`);
				onSort();
			}
		},
		[column.id, onSort, isSortable],
	);

	// Render the header content based on its type
	const headerContent = useMemo(() => {
		if (typeof column.header === "function") {
			return column.header();
		} else {
			return column.header;
		}
	}, [column]);

	// Determine which sort icon to display
	const sortIcon = useMemo(() => {
		if (!isSortable) {
			return null;
		}

		if (isSorted) {
			if (isSortedDesc) {
				return (
					<SortDesc
						className="h-4 w-4 text-[var(--table-header-foreground)]"
						aria-label="Sorted descending"
					/>
				);
			} else {
				return (
					<SortAsc
						className="h-4 w-4 text-[var(--table-header-foreground)]"
						aria-label="Sorted ascending"
					/>
				);
			}
		} else {
			return (
				<SortAsc
					className="h-4 w-4 text-[var(--table-header-muted-foreground)] opacity-30"
					aria-label="Not sorted"
				/>
			);
		}
	}, [isSortable, isSorted, isSortedDesc]);

	return (
		<TableHead
			className={`${isSortable ? "cursor-pointer" : ""} sticky top-0 z-20 ${column.headerClassName || ""} bg-[var(--table-header)] text-[var(--table-header-foreground)]`}
			onClick={handleClick}
			data-column-id={column.id}
			data-sortable={isSortable}
			data-sorted={isSorted ? (isSortedDesc ? "desc" : "asc") : "none"}
		>
			<div className="inline-flex items-center gap-1">
				{headerContent}
				{sortIcon}
			</div>
		</TableHead>
	);
}
